import { FilterDropdown } from '@ui';
import { CONTRACTOR_AFFILIATION_PREFERENCES } from '@lp/features/admin/forms/ContractorForm/types';
import { FilterKey, FilterResourceType } from '@features/components/CaseOverview/components/AssignedResources/types';
import { SelectItemType } from '@/types';
import { getResourceTypes, ResourceFilters, resourceTypeIs, SurgeonType, surgeonTypeItems } from '../../config';
import { AffiliationType, EmployeeRole } from '@/api/apis/resources';
import { Uuid } from '@/api/types';
import { getAffiliationFilterTitle, getAffiliationTypeFilterTitle, getFilterTitle } from './utils';
import { ResourceSubfilter } from './ResourceSubfilter';
import { Hub } from '@/api/apis/facilities';

type CaseResourceFiltersProps = {
  filters: ResourceFilters;
  updateFilters: (filter: ResourceFilters[keyof ResourceFilters], type: keyof ResourceFilters) => void;
  hubsList: SelectItemType[];
  selectedHub?: Hub;
  onHubSelectChange: (hubId: string) => void;
  txCenterId?: Uuid;
};

export const CaseResourceFilters = ({
  filters,
  updateFilters,
  hubsList,
  selectedHub,
  onHubSelectChange,
  txCenterId,
}: CaseResourceFiltersProps) => {
  const resourceTypes = getResourceTypes(txCenterId);

  const onSurgeonTypeChange = (value: SurgeonType) => {
    updateFilters([value], FilterKey.SurgeonType);
  };

  const onAffiliationChange = (value: AffiliationType) => {
    updateFilters(value ? [value] : [], FilterKey.AffiliationType);
  };

  const showHubsFilter =
    (resourceTypeIs(FilterResourceType.TmdxContacts, filters) &&
      filters.tmdxUser[0] === EmployeeRole.ClinicalSpecialist) ||
    resourceTypeIs(FilterResourceType.Devices, filters);

  const showSurgeonFilter =
    resourceTypeIs(FilterResourceType.TmdxContacts, filters) &&
    filters.tmdxUser?.[0] === EmployeeRole.RetrievalSurgeon &&
    surgeonTypeItems;

  const showAffiliationFilter =
    showSurgeonFilter &&
    filters.tmdxUser?.[0] === EmployeeRole.RetrievalSurgeon &&
    filters.surgeonType?.[0] === SurgeonType.Contractor;

  return (
    <div className="flex justify-between">
      <div className="flex gap-10">
        <FilterDropdown
          isFaded
          title={filters.resourceType.length ? getFilterTitle(resourceTypes, filters.resourceType[0]) : 'Resource Type'}
          items={resourceTypes}
          selectedKeys={filters.resourceType}
          mode="single"
          showCounter={false}
          onSelectedKeysChange={(values) => updateFilters(values, FilterKey.ResourceType)}
        />
        <ResourceSubfilter filters={filters} updateFilters={updateFilters} />
        {showHubsFilter && (
          <FilterDropdown
            isFaded
            title={selectedHub?.id ? getFilterTitle(hubsList, selectedHub.id) : 'NOP Hub'}
            items={hubsList}
            selectedKeys={selectedHub?.id ? [selectedHub.id] : []}
            mode="single"
            showCounter={false}
            onSelectedKeysChange={(values) => onHubSelectChange(values[0])}
          />
        )}

        {showSurgeonFilter && (
          <FilterDropdown
            title={getAffiliationFilterTitle(filters)}
            disallowEmptySelection
            items={surgeonTypeItems}
            selectedKeys={filters.surgeonType}
            mode="single"
            showCounter={false}
            onSelectedKeysChange={(values) => onSurgeonTypeChange(values[0] as SurgeonType)}
          />
        )}

        {showAffiliationFilter && (
          <FilterDropdown
            title={getAffiliationTypeFilterTitle(filters)}
            items={CONTRACTOR_AFFILIATION_PREFERENCES}
            selectedKeys={filters.affiliationType ?? []}
            mode="single"
            showCounter={false}
            onSelectedKeysChange={(values) => {
              onAffiliationChange(values[0] as AffiliationType);
            }}
          />
        )}
      </div>
    </div>
  );
};
